import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
    Button,
    CircularProgress,
    InputAdornment,
    TextField,
    useTheme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks'

import { Container } from '../../../components'
import { useProductProfiles, useUser } from '../../../hooks'
import { ProductProfileListItem } from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'
import { CreateProductProfileModal } from './CreateProductProfileModal'
import { ProductProfileDetailModal } from './ProductProfileDetailModal'
import { ProductProfileRow } from './ProductProfileRow'

export const ProductLibraryHome = () => {
    const theme = useTheme()
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace.id ?? null

    const {
        profiles,
        loading,
        selectedProfile,
        detailLoading,
        loadProfiles,
        loadProfileDetail,
        createProfile,
        updateProfile,
        removeProfile,
        uploadDocument,
        removeDocument,
        clearSelectedProfile,
        updateSelectedProfile,
    } = useProductProfiles()

    const [searchText, setSearchText] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [createOpen, setCreateOpen] = useState(false)
    const [detailOpen, setDetailOpen] = useState(false)

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearch(searchText.trim())
        }, 300)
        return () => window.clearTimeout(timer)
    }, [searchText])

    useEffect(() => {
        if (!apartmentId) {
            return
        }
        loadProfiles({
            apartment_id: apartmentId,
            search: debouncedSearch || undefined,
        }).catch(() => {
            toast.error('Unable to load product library')
        })
    }, [apartmentId, debouncedSearch, loadProfiles])

    const handleOpenProfile = async (profile: ProductProfileListItem) => {
        setDetailOpen(true)
        try {
            await loadProfileDetail(profile.id)
        } catch {
            toast.error('Unable to load product details')
            setDetailOpen(false)
        }
    }

    const handleCreateProfile = async (
        payload: Parameters<typeof createProfile>[0],
    ) => {
        const profile = await createProfile(payload)
        toast.success('Product created')
        if (apartmentId) {
            await loadProfiles({
                apartment_id: apartmentId,
                search: debouncedSearch || undefined,
            })
        }
        setDetailOpen(true)
        await loadProfileDetail(profile.id)
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                overflow: 'hidden',
                height: '100%',
                backgroundColor: theme.palette.grey[50],
            }}
        >
            <Container
                style={{
                    padding: theme.spacing(1.5, 2.5),
                    gap: theme.spacing(2),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
            >
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search brand, model, or name..."
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    fontSize="small"
                                    style={{
                                        color: theme.palette.text.disabled,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                    style={{ minWidth: 280, flex: 1, maxWidth: 420 }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateOpen(true)}
                    style={{
                        marginLeft: 'auto',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 8,
                    }}
                >
                    Add product
                </Button>
            </Container>

            {loading ? (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: theme.spacing(1.5),
                    }}
                >
                    <CircularProgress size={36} />
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.text.secondary,
                        }}
                    >
                        Loading products...
                    </span>
                </Container>
            ) : profiles.length === 0 ? (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: theme.spacing(2),
                    }}
                >
                    <LibraryBooksIcon
                        style={{
                            fontSize: 56,
                            color: theme.palette.grey[300],
                        }}
                    />
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                        }}
                    >
                        {debouncedSearch
                            ? 'No products match your search'
                            : 'No products in the library yet'}
                    </span>
                    {!debouncedSearch && (
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.disabled,
                                maxWidth: 360,
                                textAlign: 'center',
                            }}
                        >
                            Add products to store manuals, warranties, and spec
                            sheets that auto-link to matching inventory assets.
                        </span>
                    )}
                    {!debouncedSearch && (
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateOpen(true)}
                            style={{
                                textTransform: 'none',
                                fontWeight: 500,
                                borderRadius: 8,
                                marginTop: theme.spacing(1),
                            }}
                        >
                            Add your first product
                        </Button>
                    )}
                </Container>
            ) : (
                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                        overflow: 'auto',
                        padding: theme.spacing(2.5),
                        gap: theme.spacing(1.5),
                    }}
                >
                    {profiles.map((profile) => (
                        <ProductProfileRow
                            key={profile.id}
                            profile={profile}
                            onClick={handleOpenProfile}
                        />
                    ))}
                </Container>
            )}

            <CreateProductProfileModal
                open={createOpen}
                apartmentId={apartmentId}
                onClose={() => setCreateOpen(false)}
                onCreate={handleCreateProfile}
            />

            <ProductProfileDetailModal
                open={detailOpen}
                profile={selectedProfile}
                loading={detailLoading}
                onClose={() => {
                    setDetailOpen(false)
                    clearSelectedProfile()
                }}
                onSaveProfile={updateProfile}
                onUploadDocument={uploadDocument}
                onDeleteDocument={removeDocument}
                onDeleteProfile={async (profileId) => {
                    await removeProfile(profileId)
                    if (apartmentId) {
                        await loadProfiles({
                            apartment_id: apartmentId,
                            search: debouncedSearch || undefined,
                        })
                    }
                }}
                onProfileUpdated={updateSelectedProfile}
            />
        </Container>
    )
}
